import { supabase } from './supabase';
import type { CoverLetter, OutputLanguage, SavedCoverLetter, StoredLetterAnalysis } from '../types';

/**
 * Persistance des lettres — modèle de services/cv.ts. RLS : `user_id = auth.uid()`.
 *
 * `content` porte la lettre validée, `analyse` déjà retiré côté serveur. Le nom et la fonction du
 * destinataire y figurent : ce sont les données d'un TIERS, déclarées au § 2.2 de la politique.
 * Elles ne sont exploitées nulle part ailleurs — ni analytics, ni agrégat admin — et disparaissent
 * avec la lettre ou avec le compte.
 */
export async function saveCoverLetter(
  letter: CoverLetter,
  title: string,
  // Détermine la typographie appliquée à l'export PDF : les règles françaises de
  // coverLetterPdf sont inertes sur un texte anglais, mais par coïncidence, pas par contrat.
  outputLanguage: OutputLanguage = 'fr',
): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Tu dois être connecté pour enregistrer ta lettre.');

  const { data, error } = await supabase
    .from('cover_letters')
    .insert({ user_id: user.id, title, content: letter, output_language: outputLanguage })
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

// `limite` : les plus récentes seulement (hub « Ma lettre », qui n'en montre que deux).
export async function listCoverLetters(limite?: number): Promise<SavedCoverLetter[]> {
  const requete = supabase
    .from('cover_letters')
    .select('id, title, created_at, content, output_language')
    .order('created_at', { ascending: false });
  const { data, error } = await (limite ? requete.limit(limite) : requete);
  if (error) throw error;
  return (data as SavedCoverLetter[]) ?? [];
}

export async function countCoverLetters(): Promise<number> {
  const { count, error } = await supabase
    .from('cover_letters')
    .select('id', { count: 'exact', head: true });
  if (error) throw error;
  return count ?? 0;
}

// Suppression unitaire : c'est le seul levier réel sur les données du destinataire, le nom
// figurant de toute façon dans le corps de la lettre.
export async function deleteCoverLetter(id: string): Promise<void> {
  const { error } = await supabase.from('cover_letters').delete().eq('id', id);
  if (error) throw error;
}

/**
 * Analyse persistée d'une lettre.
 *
 * Table `cover_letter_analyses` : lecture seule côté client, AUCUNE policy d'écriture — c'est ce
 * qui rend le score digne de foi. Elle disparaît avec la lettre par cascade, ce que le § 6 de la
 * politique promet.
 */
export async function getCoverLetterAnalysis(
  coverLetterId: string,
): Promise<StoredLetterAnalysis | null> {
  const { data, error } = await supabase
    .from('cover_letter_analyses')
    .select('cover_letter_id, created_at, empreinte, diagnostic')
    .eq('cover_letter_id', coverLetterId)
    .maybeSingle();
  if (error) throw error;
  return (data as StoredLetterAnalysis | null) ?? null;
}

/** Dernière analyse toutes lettres confondues, pour l'anneau de l'écran « Ma lettre ». */
export async function getLastCoverLetterAnalysis(): Promise<StoredLetterAnalysis | null> {
  const { data, error } = await supabase
    .from('cover_letter_analyses')
    .select('cover_letter_id, created_at, empreinte, diagnostic')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as StoredLetterAnalysis | null) ?? null;
}
